/**
 * Builds HTML for Quarterly Scorecard PDF export (print dialog).
 * Uses the same pattern as meetingNotesPdf.ts — open new window, write HTML, trigger print.
 */

import type { ScorecardMetric, QuarterInfo } from '../types';

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatValue(value: number | null | undefined, unit: string | null): string {
  if (value === null || value === undefined) return '—';
  const cleanUnit = unit ? unit.replace(/^#+\s*|\s*#+$/g, '').trim().toLowerCase() : '';
  if ((cleanUnit === 'hours' || cleanUnit === 'hr' || cleanUnit === 'hrs') && typeof value === 'number') {
    const totalSeconds = Math.round(Math.abs(value) * 3600);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const sign = value < 0 ? '-' : '';
    return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  if (cleanUnit === '$' && typeof value === 'number') {
    if (value < 0) return `-$${Math.abs(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  const formatted = typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(value);
  return cleanUnit ? `${formatted} ${cleanUnit}` : formatted;
}

function formatPace(metric: ScorecardMetric, weekKey: string | undefined, _weeks: string[]): string {
  if (!weekKey || !metric.quarter_target) return '—';
  const week = metric.weeks.find((w) => w.week_start === weekKey);
  if (!week) return '—';

  let delta: number;
  if (metric.target_type === 'threshold') {
    delta = week.average_actual - week.weekly_target;
  } else {
    delta = metric.evaluation_mode === 'rolling'
      ? week.average_actual - week.average_target
      : week.cumulative_actual - week.cumulative_target;
  }

  const formatted = formatValue(Math.abs(delta), null);
  return delta > 0 ? `+${formatted}` : delta < 0 ? `-${formatted}` : '0';
}

export interface ScorecardPdfData {
  scorecard: ScorecardMetric[];
  weeks: string[];
  quarterInfo: QuarterInfo | null;
  departmentName?: string;
}

export function buildScorecardHtml(data: ScorecardPdfData): string {
  const { scorecard, weeks, quarterInfo, departmentName } = data;
  const q = quarterInfo?.quarter ?? 1;
  const y = quarterInfo?.year ?? new Date().getFullYear();
  const title = `Quarterly Scorecard — Q${q} ${y}`;
  const deptLabel = departmentName ? escapeHtml(departmentName) : 'All Departments';

  // Limit to 13 weeks for print
  const printWeeks = weeks.slice(0, 13);

  const rows = scorecard.map((metric) => {
    const metricWeeks = printWeeks.map((sunday) => metric.weeks.find((w) => w.week_start === sunday));
    const isThreshold = metric.target_type === 'threshold';
    const owner = metric.department_name || '—';

    // Pace: use current week or last week with data
    const currentWeekKey = printWeeks[printWeeks.length - 1];
    const paceStr = formatPace(metric, currentWeekKey, printWeeks);

    const weekCells = metricWeeks.map((week) => {
      const isOnTrack = week
        ? isThreshold
          ? metric.threshold_direction === 'below'
            ? (week.weekly_actual ?? 0) <= week.weekly_target
            : (week.weekly_actual ?? 0) >= week.weekly_target
          : (week.weekly_actual ?? 0) >= week.weekly_target
        : false;
      const hasValue = week && week.weekly_actual != null;
      const cellClass = hasValue ? (isOnTrack ? 'on-track' : 'off-track') : '';

      return `<td class="${cellClass}">${escapeHtml(formatValue(week?.weekly_actual, metric.unit))}</td>`;
    }).join('');

    return `
      <tr>
        <td>${escapeHtml(metric.metric_name)}</td>
        <td>${escapeHtml(owner)}</td>
        <td>${escapeHtml(formatValue(metric.quarter_target, metric.unit))}</td>
        ${weekCells}
        <td class="pace">${escapeHtml(paceStr)}</td>
      </tr>
    `;
  }).join('');

  const weekHeaders = printWeeks.map((w) => {
    const d = new Date(w + 'T00:00:00');
    const end = new Date(d);
    end.setDate(end.getDate() + 6);
    const startStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `<th>${startStr}–${endStr}</th>`;
  }).join('');

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(title)}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 24px; color: #1a1a1e; line-height: 1.4; }
    h1 { font-size: 22px; border-bottom: 2px solid #f76100; padding-bottom: 8px; margin-bottom: 4px; color: #1a1a1e; }
    .subtitle { font-size: 13px; color: #666; margin-bottom: 16px; }
    table.scorecard-table { width: 100%; border-collapse: collapse; font-size: 11px; }
    table.scorecard-table th, table.scorecard-table td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
    table.scorecard-table th { background: #f5f5f5; font-weight: 600; color: #333; }
    table.scorecard-table th:nth-child(n+4) { text-align: center; }
    table.scorecard-table td:nth-child(n+4) { text-align: center; }
    table.scorecard-table .on-track { color: #2e7d32; font-weight: 600; }
    table.scorecard-table .off-track { color: #d32f2f; font-weight: 600; }
    table.scorecard-table .pace { font-weight: 600; color: #555; }
    .footer { margin-top: 24px; font-size: 10px; color: #999; }
    @media print {
      body { padding: 16px; }
      .footer { margin-top: 16px; }
    }
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <p class="subtitle">${deptLabel} — Exported from BSG EOS</p>

  <table class="scorecard-table">
    <thead>
      <tr>
        <th>Metric</th>
        <th>Owner</th>
        <th>Target</th>
        ${weekHeaders}
        <th>Pace</th>
      </tr>
    </thead>
    <tbody>
      ${rows}
    </tbody>
  </table>

  <p class="footer">BSG EOS — ${new Date().toLocaleString()}</p>
</body>
</html>
  `.trim();
}
